// Import necessary React hooks and components.
import React, { createContext, useEffect, useState, useCallback } from "react";
// Import utility for simplifying track points.
import { simplifyPoints } from "~/js/utilities/simplifyPoints";
// Import constants for point simplification thresholds.
import { SIMPLER_THRESHOLD, SIMPLEST_THRESHOLD } from "~/js/config/GPXMapping";
// Import GPX parsing function.
import { parseGPX } from "~/js/Views/Track/Parser/parseGPX";
import { parseTCX } from "~/js/Views/Track/Parser/parseTCX";
// Import utility for flattening nested objects.
import { getFlattenObject } from "~/js/utilities/flattenObject";

// Import IndexedDB helper functions for database operations.
import {
  openDB,
  addTrackToDB,
  getAllFromDB,
  getTrackFromDB,
  deleteTracksFromDB,
  updateTrackInDB,
  updateTrackGroupInDB,
  deleteTrackGroupFromDB,
} from "~/js/utilities/storage/indexeddb";

// Import local storage helper functions for managing track IDs.
import {
  setTrackIDtoLS,
  deleteTrackIDfromLS,
  getTrackIDfromLS,
} from "~/js/utilities/storage/localStorage";
// Import utility for getting activity details from track type.
import { getActivityDetails } from "~/js/config/activityMapping";
// Import utility for calculating geographical bounds of tracks.
import { calculateTracksBounds } from "~/js/utilities/calculations/calculateBounds";
// Import utility for calculating base statistics of a track.
import { getBaseStatistics } from "~/js/utilities/calculations/trackStatistics/getBaseStatistics";
import { prettifyName } from "~/js/Views/Track/Parser/utilities/buildTrackData";

// Create a React Context for import-related state.
export const ImportContext = createContext();

/**
 * Provides import-related state and actions to its children components.
 * This provider manages the state of imported GPX files, processes them,
 * and stores the resulting track data in IndexedDB.
 */
export const ImportProvider = ({ children }) => {
  // State for files that have been imported but not yet processed.
  const [importedFiles, setImportedFilesState] = useState([]);
  const [shouldGroupFiles, setShouldGroupFiles] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const clearImportErrors = useCallback(() => setImportErrors([]), []);

  const setImportedFiles = useCallback((files, group = false) => {
    setShouldGroupFiles(group);
    setImportedFilesState(files);
  }, []);

  // State for the index of all tracks, used for display lists.
  const [tracksIndex, setTracksIndex] = useState([]);
  const [tracksBounds, setTracksBounds] = useState({});
  // State for tracked group names mapped to their ID from the DB
  const [trackGroups, setTrackGroups] = useState([]);
  // State for the currently selected full track data.
  const [track, setTrack] = useState(null);
  // State for the ID of the currently selected track.
  const [trackID, setTrackID] = useState(null);
  // State to indicate if files are currently being processed.
  const [isProcessing, setIsProcessing] = useState(false);
  // State to indicate if the database is currently loading data.
  const [isDBLoading, setIsDBLoading] = useState(true);

  // Effect hook to load the tracks index from IndexedDB when the component mounts.
  useEffect(() => {
    const loadTracksFromDB = async () => {
      setIsDBLoading(true);
      try {
        const storedTracksIndex = (await getAllFromDB("tracksIndex")) || [];
        setTracksIndex(storedTracksIndex);

        let storedTrackGroups = (await getAllFromDB("trackGroups")) || [];

        // Self-Healing Migration: Extract inferred track groups and convert them into properly stored explicit table rows
        const newlyDiscoveredGroups = [];
        storedTracksIndex.forEach((track) => {
          const gid = track["info.groupId"];
          if (gid) {
            const exists = storedTrackGroups.find((g) => g.id === gid);
            const inNew = newlyDiscoveredGroups.find((g) => g.id === gid);
            if (!exists && !inNew) {
              newlyDiscoveredGroups.push({
                id: gid,
                name: track["info.groupName"] || track["info.name"],
              });
            }
          }
        });

        if (newlyDiscoveredGroups.length > 0) {
          for (const newGroup of newlyDiscoveredGroups) {
            await updateTrackGroupInDB(newGroup);
          }
          storedTrackGroups = [...storedTrackGroups, ...newlyDiscoveredGroups];
        }

        setTrackGroups(storedTrackGroups);

        // Calculate and set the combined bounds of all tracks.
        const tmpBounds = storedTracksIndex.map((track) => track.bounds);
        setTracksBounds(calculateTracksBounds(tmpBounds));
      } catch (error) {
        console.error("Failed to load tracksIndex from IndexedDB:", error);
      } finally {
        setIsDBLoading(false);
      }
    };
    loadTracksFromDB();
  }, []); // Empty dependency array ensures this runs only once on mount.

  const [newlyImportedGroups, setNewlyImportedGroups] = useState([]);

  // ... (existing helper function)

  // ... (existing useEffect for loading DB)

  // Effect hook to process imported GPX files.
  useEffect(() => {
    if (importedFiles.length > 0) {
      setIsProcessing(true);
      const newlyBuiltTracks = [];
      // Clear previous newly imported groups
      setNewlyImportedGroups([]);

      const processFiles = async () => {
        // ... (existing batch group ID logic)
        const batchGroupId = shouldGroupFiles ? crypto.randomUUID() : null;

        const BATCH_SIZE = 5;
        for (let i = 0; i < importedFiles.length; i += BATCH_SIZE) {
          // ... (existing processing loop)
          const batch = importedFiles.slice(i, i + BATCH_SIZE);

          for (const currentFile of batch) {
            // ... (existing parsing and track extraction)
            try {
              let gpxParsed;
              if (currentFile.fileName.toLowerCase().endsWith(".tcx")) {
                gpxParsed = parseTCX(currentFile.content);
              } else {
                gpxParsed = parseGPX(currentFile.content);
              }
              // ...
              gpxParsed.file = {
                fileName: currentFile.fileName,
                fileSize: currentFile.fileSize,
                fileType: currentFile.fileType,
                fileImportDate: currentFile.fileImportDate,
                fileLastModifiedDate: currentFile.fileLastModifiedDate,
              };
              console.log("gpx parser import", gpxParsed);

              let tracksToUse = gpxParsed.tracks;

              if (
                (!tracksToUse || tracksToUse.length === 0) &&
                gpxParsed.routes &&
                gpxParsed.routes.length > 0
              ) {
                tracksToUse = gpxParsed.routes.map((route) => ({
                  trackInfo: route.routeInfo,
                  trackData: route.routeData,
                }));
              }

              if (!tracksToUse || tracksToUse.length === 0) {
                console.warn(
                  `No track or route data found in ${currentFile.fileName}. Skipping.`,
                );
                setImportErrors((prev) => [
                  ...prev,
                  { fileName: currentFile.fileName, type: "NO_DATA" },
                ]);
                continue;
              }

              // Use the batch group ID if enabled and file has only one track, otherwise generate one per file
              const fileGroupId =
                batchGroupId && tracksToUse.length === 1
                  ? batchGroupId
                  : crypto.randomUUID();

              for (let i = 0; i < tracksToUse.length; i++) {
                const curTrackData = tracksToUse[i];
                // ... (point simplification)
                const trackSimpler = simplifyPoints(
                  curTrackData.trackData,
                  SIMPLER_THRESHOLD,
                );
                const waypoints = gpxParsed.waypoints || [];
                const tmpName =
                  curTrackData.trackInfo.name || gpxParsed.file.fileName;

                // Store original name for future rebuilds
                curTrackData.trackInfo.originalName = tmpName;

                let baseName = prettifyName(tmpName);
                if (tracksToUse.length > 1) {
                  baseName += ` ${i + 1}`;
                }
                curTrackData.trackInfo.name = baseName;

                // Assign the group ID (and null groupName initially)
                curTrackData.trackInfo.groupId = fileGroupId;
                curTrackData.trackInfo.groupName = null;

                // ... (activity details)
                curTrackData.trackInfo.activity = getActivityDetails(
                  curTrackData.trackInfo.type,
                );

                // ... (build track object)
                const builtTrack = {
                  id: crypto.randomUUID(),
                  metadata: gpxParsed.metadata,
                  file: gpxParsed.file,
                  info: curTrackData.trackInfo,
                  marker: { wayPoints: waypoints },
                  points: { points: curTrackData.trackData },
                };

                const stats = getBaseStatistics(trackSimpler);
                // ... (build flatten track)
                const flattenTrack = {
                  id: builtTrack.id,
                  wayPointsCount: builtTrack.marker.wayPoints.length,
                  ...getFlattenObject(stats.statistics, "stats"),
                  ...getFlattenObject(stats.spatial, "spatial"),
                  ...getFlattenObject(builtTrack.file, "file"),
                  ...getFlattenObject(builtTrack.info, "info"),
                  ...getFlattenObject(builtTrack.metadata, "metadata"),
                  pointsSimplified: simplifyPoints(
                    curTrackData.trackData,
                    SIMPLEST_THRESHOLD,
                  ),
                  bounds: stats.spatial.boundaries,
                };

                await addTrackToDB(builtTrack, flattenTrack);
                newlyBuiltTracks.push(builtTrack);
              }
            } catch (error) {
              console.error(
                `Error processing file ${currentFile.fileName}:`,
                error,
              );
              setImportErrors((prev) => [
                ...prev,
                {
                  fileName: currentFile.fileName,
                  type: "PARSE_ERROR",
                  message: error?.message || "Unknown error",
                },
              ]);
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 0));
        }

        if (newlyBuiltTracks.length > 0) {
          const updatedTracksIndex = await getAllFromDB("tracksIndex");
          setTracksIndex(updatedTracksIndex);

          // Identify newly created groups that have more than 1 track
          const groups = {};
          newlyBuiltTracks.forEach((track) => {
            const gid = track.info.groupId;
            if (gid) {
              if (!groups[gid]) {
                groups[gid] = {
                  id: gid,
                  name: track.info.name,
                  count: 0,
                  trackIds: [],
                };
              }
              groups[gid].count++;
              groups[gid].trackIds.push(track.id);
            }
          });

          const multiTrackGroups = Object.values(groups).filter(
            (g) => g.count > 1,
          );

          // Insert the groups directly into indexDB store
          for (const grp of multiTrackGroups) {
            const groupToAdd = { id: grp.id, name: grp.name };
            await updateTrackGroupInDB(groupToAdd);
          }
          const loadedGroups = (await getAllFromDB("trackGroups")) || [];
          setTrackGroups(loadedGroups);

          setNewlyImportedGroups(multiTrackGroups);

          const lastAddedTrackId =
            newlyBuiltTracks[newlyBuiltTracks.length - 1].id;
          setTrackID(lastAddedTrackId);
          setTrackIDtoLS(lastAddedTrackId);
        } else {
          console.log("No new tracks were built from the imported files.");
        }
      };

      processFiles().finally(() => {
        setImportedFilesState([]);
        setShouldGroupFiles(false);
        setIsProcessing(false);
      });
    }
  }, [importedFiles, shouldGroupFiles]);

  // ... (existing updateTrack logic)

  /**
   * Updates the group name for a specific group, executing an O(1) change rather than iterating tracks.
   * @param {string} groupId The ID of the group to update.
   * @param {string} newGroupName The new name for the group.
   */
  const updateGroupName = useCallback(async (groupId, newGroupName) => {
    try {
      const groupToUpdate = { id: groupId, name: newGroupName };
      await updateTrackGroupInDB(groupToUpdate);

      // Update context state
      const updatedGroups = (await getAllFromDB("trackGroups")) || [];
      setTrackGroups(updatedGroups);
    } catch (error) {
      console.error("Failed to update group name:", error);
    }
  }, []);

  /**
   * Removes the group association from all tracks in a specific group.
   * @param {string} groupId The ID of the group to ungroup.
   */
  const ungroupTracks = useCallback(
    async (groupId) => {
      try {
        // First delete it from the standalone track groups index
        await deleteTrackGroupFromDB(groupId);
        const updatedGroups = (await getAllFromDB("trackGroups")) || [];
        setTrackGroups(updatedGroups);

        const indexToUpdate = tracksIndex.filter(
          (t) => t["info.groupId"] === groupId,
        );

        // Update each track in the group
        for (const indexEntry of indexToUpdate) {
          const trackId = indexEntry.id;
          const fullTrack = await getTrackFromDB(trackId);

          if (fullTrack) {
            fullTrack.info.groupId = null;
            fullTrack.info.groupName = null;
            const updatedIndexEntry = {
              ...indexEntry,
            };
            updatedIndexEntry["info.groupId"] = null;
            updatedIndexEntry["info.groupName"] = null;

            await updateTrackInDB(fullTrack, updatedIndexEntry);
          }
        }

        // Update context state
        const updatedIndex = await getAllFromDB("tracksIndex");
        setTracksIndex(updatedIndex);

        // Update current track if part of the group
        if (track && track.info.groupId === groupId) {
          const reloadTrack = await getTrackFromDB(track.id);
          setTrack(reloadTrack);
        }
      } catch (error) {
        console.error("Failed to ungroup tracks:", error);
      }
    },
    [tracksIndex, track],
  );

  /**
   * Removes the group association from a specific track.
   * @param {string} trackId The ID of the track to ungroup.
   */
  const ungroupTrack = useCallback(
    async (trackId) => {
      try {
        const indexEntry = tracksIndex.find((t) => t.id === trackId);
        if (!indexEntry) return;

        const fullTrack = await getTrackFromDB(trackId);

        if (fullTrack) {
          fullTrack.info.groupId = null;
          fullTrack.info.groupName = null;
          const updatedIndexEntry = {
            ...indexEntry,
          };
          updatedIndexEntry["info.groupId"] = null;
          updatedIndexEntry["info.groupName"] = null;

          await updateTrackInDB(fullTrack, updatedIndexEntry);
        }

        // Update context state
        const updatedIndex = await getAllFromDB("tracksIndex");
        setTracksIndex(updatedIndex);

        // Update current track if it's the one modified
        if (track && track.id === trackId) {
          const reloadTrack = await getTrackFromDB(track.id);
          setTrack(reloadTrack);
        }
      } catch (error) {
        console.error("Failed to ungroup track:", error);
      }
    },
    [tracksIndex, track],
  );

  /**
   * Adds the group association to a specific track.
   * @param {string} trackId The ID of the track to group.
   * @param {string} groupId The ID of the group to apply.
   * @param {string} groupName The Name of the group to apply.
   */
  const groupTrack = useCallback(
    async (trackId, groupId, groupName) => {
      try {
        // Note: Check if the provided group actually exists in trackGroups,
        // if not, add it explicitly to ensure our normalized DB holds the record correctly.
        const storedGroups = trackGroups;
        if (!storedGroups.find((g) => g.id === groupId)) {
          await updateTrackGroupInDB({ id: groupId, name: groupName });
          const reloadedGroups = (await getAllFromDB("trackGroups")) || [];
          setTrackGroups(reloadedGroups);
        }

        const indexEntry = tracksIndex.find((t) => t.id === trackId);
        if (!indexEntry) return;

        const fullTrack = await getTrackFromDB(trackId);

        if (fullTrack) {
          fullTrack.info.groupId = groupId;
          // We can eventually remove this later, but we keep info.groupName synced back here for back-compat
          fullTrack.info.groupName = groupName;
          const updatedIndexEntry = {
            ...indexEntry,
          };
          updatedIndexEntry["info.groupId"] = groupId;
          updatedIndexEntry["info.groupName"] = groupName;

          await updateTrackInDB(fullTrack, updatedIndexEntry);
        }

        // Update context state
        const updatedIndex = await getAllFromDB("tracksIndex");
        setTracksIndex(updatedIndex);

        // Update current track if it's the one modified
        if (track && track.id === trackId) {
          const reloadTrack = await getTrackFromDB(track.id);
          setTrack(reloadTrack);
        }
      } catch (error) {
        console.error("Failed to group track:", error);
      }
    },
    [tracksIndex, track, trackGroups],
  );

  /**
   * Adds the group association to multiple tracks at once.
   * @param {string[]} trackIds Array of track IDs to group.
   * @param {string} groupId The ID of the group to apply.
   * @param {string} groupName The Name of the group to apply.
   */
  const groupTracks = useCallback(
    async (trackIds, groupId, groupName) => {
      console.log("groupTracks batch triggered", {
        trackIds,
        groupId,
        groupName,
      });
      try {
        if (!Array.isArray(trackIds) || trackIds.length === 0) return;

        // Ensure group exists
        const storedGroups = trackGroups;
        if (!storedGroups.find((g) => g.id === groupId)) {
          await updateTrackGroupInDB({ id: groupId, name: groupName });
          const reloadedGroups = (await getAllFromDB("trackGroups")) || [];
          setTrackGroups(reloadedGroups);
        }

        let updatedCurrentTrack = false;

        for (const trackId of trackIds) {
          const indexEntry = tracksIndex.find((t) => t.id === trackId);
          if (!indexEntry) continue;

          const fullTrack = await getTrackFromDB(trackId);
          if (fullTrack) {
            fullTrack.info.groupId = groupId;
            fullTrack.info.groupName = groupName;

            const updatedIndexEntry = { ...indexEntry };
            updatedIndexEntry["info.groupId"] = groupId;
            updatedIndexEntry["info.groupName"] = groupName;

            await updateTrackInDB(fullTrack, updatedIndexEntry);

            if (track && track.id === trackId) {
              updatedCurrentTrack = true;
            }
          }
        }

        // Batch update context state
        const updatedIndex = await getAllFromDB("tracksIndex");
        setTracksIndex(updatedIndex);

        if (updatedCurrentTrack) {
          const reloadTrack = await getTrackFromDB(track.id);
          setTrack(reloadTrack);
        }
      } catch (error) {
        console.error("Failed to group multiple tracks:", error);
      }
    },
    [tracksIndex, track, trackGroups],
  );

  /**
   * Removes the group association from multiple specific tracks.
   * @param {string[]} trackIds Array of track IDs to ungroup.
   */
  const ungroupSelectedTracks = useCallback(
    async (trackIds) => {
      try {
        if (!Array.isArray(trackIds) || trackIds.length === 0) return;

        let updatedCurrentTrack = false;

        for (const trackId of trackIds) {
          const indexEntry = tracksIndex.find((t) => t.id === trackId);
          if (!indexEntry) continue;

          const fullTrack = await getTrackFromDB(trackId);
          if (fullTrack) {
            fullTrack.info.groupId = null;
            fullTrack.info.groupName = null;

            const updatedIndexEntry = { ...indexEntry };
            updatedIndexEntry["info.groupId"] = null;
            updatedIndexEntry["info.groupName"] = null;

            await updateTrackInDB(fullTrack, updatedIndexEntry);

            if (track && track.id === trackId) {
              updatedCurrentTrack = true;
            }
          }
        }

        // Batch update context state
        const updatedIndex = await getAllFromDB("tracksIndex");
        setTracksIndex(updatedIndex);

        if (updatedCurrentTrack) {
          const reloadTrack = await getTrackFromDB(track.id);
          setTrack(reloadTrack);
        }
      } catch (error) {
        console.error("Failed to ungroup multiple tracks:", error);
      }
    },
    [tracksIndex, track],
  );

  // Effect hook to load the full track data when the trackID changes.
  useEffect(() => {
    const loadFullTrack = async () => {
      if (trackID) {
        const selectedTrack = await getTrackFromDB(trackID);
        if (selectedTrack) {
          if (track?.id !== selectedTrack.id) {
            setTrack(selectedTrack);
            setTrackIDtoLS(trackID);
          }
        } else {
          console.warn(`Track with ID ${trackID} not found in DB.`);
          setTrack(null);
          setTrackID(null);
          deleteTrackIDfromLS();
        }
      } else {
        setTrack(null);
      }
    };

    if (!isDBLoading) {
      loadFullTrack();
    }
  }, [trackID, isDBLoading, track?.id]);

  /**
   * Deletes multiple tracks from the database and updates the state.
   * @param {string[]} idsToDelete An array of track IDs to delete.
   */
  const deleteTracks = useCallback(
    async (idsToDelete) => {
      if (!Array.isArray(idsToDelete) || idsToDelete.length === 0) {
        return;
      }

      try {
        await deleteTracksFromDB(idsToDelete);

        const remainingTracks = tracksIndex.filter(
          (t) => !idsToDelete.includes(t.id),
        );
        setTracksIndex(remainingTracks);

        const tmpBounds = remainingTracks.map((track) => track.bounds);
        setTracksBounds(calculateTracksBounds(tmpBounds));

        if (trackID && idsToDelete.includes(trackID)) {
          setTrackID(null);
          setTrack(null);
          deleteTrackIDfromLS();
        }
      } catch (error) {
        console.error(`Error deleting tracks:`, error);
      }
    },
    [trackID, setTrackID, setTrack, setTracksIndex, tracksIndex],
  );

  /**
   * Deletes a single track. This is a convenience wrapper around deleteTracks.
   * @param {string} idToDelete The ID of the track to delete.
   */
  const deleteTrack = useCallback(
    async (idToDelete) => {
      await deleteTracks([idToDelete]);
    },
    [deleteTracks],
  );

  /**
   * Updates a track in the database and updates the state.
   * @param {string} trackId The ID of the track to update.
   * @param {object} updates Object containing the fields to update (e.g., { favorite: true }).
   */
  const updateTrack = useCallback(
    async (trackId, updates) => {
      try {
        const fullTrack = await getTrackFromDB(trackId);
        if (!fullTrack) {
          console.warn(`Track with ID ${trackId} not found for update.`);
          return;
        }

        // Apply updates to full track
        // Currently assuming 'favorite' goes into 'info'
        if (updates.favorite !== undefined) {
          fullTrack.info.favorite = updates.favorite;
        }

        // Apply specific updates if needed
        // ...

        // Update the index entry
        // We find the existing index entry to preserve data
        const existingIndexEntry = tracksIndex.find((t) => t.id === trackId);
        if (!existingIndexEntry) {
          console.warn(`Index entry for track ${trackId} not found.`);
          return;
        }

        const updatedIndexEntry = { ...existingIndexEntry };
        if (updates.favorite !== undefined) {
          updatedIndexEntry["info.favorite"] = updates.favorite;
        }

        await updateTrackInDB(fullTrack, updatedIndexEntry);

        // Update tracksIndex state
        setTracksIndex((prevIndex) =>
          prevIndex.map((t) => (t.id === trackId ? updatedIndexEntry : t)),
        );

        // Update current track state if it is the one being updated
        if (track && track.id === trackId) {
          setTrack(fullTrack);
        }
      } catch (error) {
        console.error("Failed to update track:", error);
      }
    },
    [tracksIndex, track],
  );

  // Effect for debugging purposes, logs changes in tracksIndex and track state.
  useEffect(() => {
    if (tracksIndex.length > 0 && trackID === null) {
      const tmpTrackId = getTrackIDfromLS();
      if (tmpTrackId) {
        setTrackID(tmpTrackId);
      } else {
        const firstTrackId = tracksIndex[0].id;
        setTrackID(firstTrackId);
        setTrackIDtoLS(firstTrackId);
      }
    }
  }, [tracksIndex]);

  const rebuildTracksDB = useCallback(async () => {
    setIsProcessing(true);
    try {
      const allTracks = await getAllFromDB("tracks");
      for (const track of allTracks) {
        if (!track.points || !track.points.points) continue;

        // Simplify points for statistics
        const trackSimpler = simplifyPoints(
          track.points.points,
          SIMPLER_THRESHOLD,
        );

        // Update Name
        // Use originalName if available, otherwise fallback to fileName
        // This ensures regarding "Rebuild" that we have a raw source to re-prettify
        const sourceName = track.info.originalName || track.file.fileName;
        if (sourceName) {
          track.info.name = prettifyName(sourceName);
        }

        // Update Activity Details
        if (track.info.type) {
          track.info.activity = getActivityDetails(track.info.type);
        }

        // Calculate statistics
        const stats = getBaseStatistics(trackSimpler);

        // Update stats in the main track object as well (important for single track view)
        track.stats = stats.statistics;
        track.spatial = stats.spatial;
        // Also update simplified points if we want to ensure they use latest simplify algo
        track.points.pointsSimplified = simplifyPoints(
          track.points.points,
          SIMPLEST_THRESHOLD,
        );
        // Note: 'pointsSimplified' is usually stored in the flattened object, but some views might use the full track's simplified points?
        // In the original import:
        // builtTrack.points = { points: curTrackData.trackData }
        // flattenTrack.pointsSimplified = ...
        // So full track doesn't explicitly store simplified points in 'points' property usually, but let's stick to updating what we can.

        // Re-construct flattenTrack
        const flattenTrack = {
          id: track.id,
          wayPointsCount: track.marker?.wayPoints?.length || 0,
          ...getFlattenObject(stats.statistics, "stats"),
          ...getFlattenObject(stats.spatial, "spatial"),
          ...getFlattenObject(track.file, "file"), // Keep existing file info
          ...getFlattenObject(track.info, "info"), // Update with new info
          ...getFlattenObject(track.metadata, "metadata"), // Keep metadata
          pointsSimplified: simplifyPoints(
            track.points.points,
            SIMPLEST_THRESHOLD,
          ),
          bounds: stats.spatial.boundaries,
        };

        // Update DB
        await updateTrackInDB(track, flattenTrack);
      }

      // Update tracksIndex state
      const updatedIndex = await getAllFromDB("tracksIndex");
      setTracksIndex(updatedIndex);

      // Re-calculate global bounds
      const tmpBounds = updatedIndex.map((track) => track.bounds);
      setTracksBounds(calculateTracksBounds(tmpBounds));
    } catch (error) {
      console.error("Failed to rebuild database:", error);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Provide the state and functions to the children components.
  return (
    <ImportContext.Provider
      value={{
        importedFiles,
        setImportedFiles,
        setTrackID,
        trackID,
        track,
        isProcessing,
        tracksIndex,
        isDBLoading,
        deleteTrack,
        deleteTracks,
        tracksBounds,
        setTracksBounds,
        updateTrack,
        updateTrackInDB,
        newlyImportedGroups,
        setNewlyImportedGroups,
        trackGroups,
        updateGroupName,
        ungroupTracks,
        ungroupTrack,
        groupTrack,
        groupTracks,
        ungroupSelectedTracks,
        rebuildTracksDB,
        importErrors,
        clearImportErrors,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};
